/**
 * Copyright 2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ConsoleLogHandler, LogLevel, OptimizelyLogger } from './logger';
import { errorResolver, infoResolver } from '../message/message_resolver';
export const INVALID_LOG_HANDLER = 'Invalid log handler';
export const INVALID_LEVEL_PRESET = 'Invalid level preset';
const debugPreset = {
    level: LogLevel.Debug,
    infoResolver,
    errorResolver,
};
const infoPreset = {
    level: LogLevel.Info,
    infoResolver,
    errorResolver,
};
const warnPreset = {
    level: LogLevel.Warn,
    errorResolver,
};
const errorPreset = {
    level: LogLevel.Error,
    errorResolver,
};
const levelPresetSymbol = Symbol();
export const DEBUG = {
    [levelPresetSymbol]: debugPreset,
};
export const INFO = {
    [levelPresetSymbol]: infoPreset,
};
export const WARN = {
    [levelPresetSymbol]: warnPreset,
};
export const ERROR = {
    [levelPresetSymbol]: errorPreset,
};
export const extractLevelPreset = (preset) => {
    if (!preset || typeof preset !== 'object' || !preset[levelPresetSymbol]) {
        throw new Error(INVALID_LEVEL_PRESET);
    }
    return preset[levelPresetSymbol];
};
const loggerSymbol = Symbol();
const validateLogHandler = (logHandler) => {
    if (typeof logHandler !== 'object' || typeof logHandler.log !== 'function') {
        throw new Error(INVALID_LOG_HANDLER);
    }
};
export const createLogger = (config) => {
    const { level, infoResolver, errorResolver } = extractLevelPreset(config.level);
    if (config.logHandler) {
        validateLogHandler(config.logHandler);
    }
    const loggerName = 'Optimizely';
    return {
        [loggerSymbol]: new OptimizelyLogger({
            name: loggerName,
            level,
            infoMsgResolver: infoResolver,
            errorMsgResolver: errorResolver,
            logHandler: config.logHandler || new ConsoleLogHandler(),
        }),
    };
};
export const wrapLogger = (logger) => {
    return {
        [loggerSymbol]: logger,
    };
};
export const extractLogger = (logger) => {
    if (!logger || typeof logger !== 'object') {
        return undefined;
    }
    return logger[loggerSymbol];
};
export const __platforms = ['__universal__'];
//# sourceMappingURL=logger_factory.js.map